/**
 * GLYDE Voice SDK - CSS-in-JS Styles
 * 
 * Centralized styles for all UI components.
 * Uses CSS custom properties for theming.
 * 
 * @packageDocumentation
 */

/**
 * Theme color definitions
 */
export const themeColors = {
  light: {
    // Primary colors (purple/magenta)
    primary: '#c026d3',
    primaryHover: '#a21caf',
    primaryLight: '#fdf4ff',
    primaryText: '#ffffff',

    // Secondary colors (blue/cyan for text chat)
    secondary: '#0284c7',
    secondaryHover: '#0369a1',
    secondaryLight: '#f0f9ff',
    secondaryText: '#ffffff',

    // Success/active colors (green)
    success: '#059669',
    successLight: '#dcfce7',
    successText: '#166534',

    // Error colors (red)
    error: '#ef4444',
    errorHover: '#dc2626',
    errorLight: '#fef2f2',
    errorText: '#b91c1c',

    // Neutral colors
    background: '#ffffff',
    surface: '#f9fafb',
    border: '#e5e7eb',
    borderLight: '#f3f4f6',
    text: '#1f2937',
    textSecondary: '#6b7280',
    textMuted: '#9ca3af',
  },
  dark: {
    // Primary colors
    primary: '#d946ef',
    primaryHover: '#c026d3',
    primaryLight: '#3b0764',
    primaryText: '#ffffff',

    // Secondary colors
    secondary: '#38bdf8',
    secondaryHover: '#0ea5e9',
    secondaryLight: '#0c4a6e',
    secondaryText: '#ffffff',

    // Success colors
    success: '#34d399',
    successLight: '#064e3b',
    successText: '#a7f3d0',

    // Error colors
    error: '#f87171',
    errorHover: '#ef4444',
    errorLight: '#450a0a',
    errorText: '#fecaca',

    // Neutral colors
    background: '#111827',
    surface: '#1f2937',
    border: '#374151',
    borderLight: '#4b5563',
    text: '#f9fafb',
    textSecondary: '#d1d5db',
    textMuted: '#9ca3af',
  }
};

/**
 * Get CSS variables for a theme
 */
export function getThemeCSSVariables(theme: 'light' | 'dark'): string {
  const colors = themeColors[theme];
  return `
    --glyde-primary: ${colors.primary};
    --glyde-primary-hover: ${colors.primaryHover};
    --glyde-primary-light: ${colors.primaryLight};
    --glyde-primary-text: ${colors.primaryText};
    --glyde-secondary: ${colors.secondary};
    --glyde-secondary-hover: ${colors.secondaryHover};
    --glyde-secondary-light: ${colors.secondaryLight};
    --glyde-secondary-text: ${colors.secondaryText};
    --glyde-success: ${colors.success};
    --glyde-success-light: ${colors.successLight};
    --glyde-success-text: ${colors.successText};
    --glyde-error: ${colors.error};
    --glyde-error-hover: ${colors.errorHover};
    --glyde-error-light: ${colors.errorLight};
    --glyde-error-text: ${colors.errorText};
    --glyde-background: ${colors.background};
    --glyde-surface: ${colors.surface};
    --glyde-border: ${colors.border};
    --glyde-border-light: ${colors.borderLight};
    --glyde-text: ${colors.text};
    --glyde-text-secondary: ${colors.textSecondary};
    --glyde-text-muted: ${colors.textMuted};
  `;
}

/**
 * Global CSS styles to inject
 */
export const globalStyles = `
  @keyframes glyde-pulse {
    0%, 100% { opacity: 1; transform: scale(1); }
    50% { opacity: 0.5; transform: scale(1.2); }
  }

  @keyframes glyde-spin {
    to { transform: rotate(360deg); }
  }

  @keyframes glyde-fade-in {
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
  }

  @keyframes glyde-slide-up {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
  }

  @keyframes glyde-bounce {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-5px); }
  }

  @keyframes glyde-modal-fade-in {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  @keyframes glyde-modal-scale-in {
    from { opacity: 0; transform: scale(0.95); }
    to { opacity: 1; transform: scale(1); }
  }

  .glyde-modal-backdrop {
    animation: glyde-modal-fade-in 0.2s ease-out;
  }

  .glyde-modal-content {
    animation: glyde-modal-scale-in 0.2s ease-out;
  }

  /* Mobile display: full viewport with safe areas, no FAB, auto-start */
  .glyde-mobile-viewport {
    position: fixed;
    inset: 0;
    width: 100%;
    height: 100%;
    height: 100dvh;
    max-height: 100dvh;
    padding-top: env(safe-area-inset-top, 0);
    padding-right: env(safe-area-inset-right, 0);
    padding-bottom: env(safe-area-inset-bottom, 0);
    padding-left: env(safe-area-inset-left, 0);
    box-sizing: border-box;
    z-index: 10000;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    background: var(--glyde-background);
  }

  .glyde-mobile-viewport .glyde-widget-root {
    flex: 1;
    min-height: 0;
    border-radius: 0;
    border: none;
    box-shadow: none;
  }

  .glyde-fade-in {
    animation: glyde-fade-in 0.2s ease-out;
  }

  .glyde-widget-root {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
    font-size: 14px;
    line-height: 1.5;
    color: var(--glyde-text);
    box-sizing: border-box;
  }

  .glyde-widget-root *, .glyde-widget-root *::before, .glyde-widget-root *::after {
    box-sizing: border-box;
  }

  .glyde-spinner {
    width: 32px;
    height: 32px;
    border: 3px solid var(--glyde-border);
    border-top-color: var(--glyde-primary);
    border-radius: 50%;
    animation: glyde-spin 1s linear infinite;
  }

  .glyde-pulse-dot {
    animation: glyde-pulse 1.5s infinite;
  }

  .glyde-fade-in {
    animation: glyde-fade-in 0.3s ease-out;
  }

  .glyde-slide-up {
    animation: glyde-slide-up 0.3s ease-out;
  }
`;

/**
 * Component-specific style objects (for inline styles)
 */
export const componentStyles = {
  // Floating widget button
  floatingButton: {
    position: 'fixed' as const,
    width: '60px',
    height: '60px',
    borderRadius: '50%',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    transition: 'transform 0.2s, box-shadow 0.2s',
    zIndex: 9999,
  },

  // Widget panel
  widgetPanel: {
    position: 'fixed' as const,
    width: '380px',
    height: '600px',
    borderRadius: '16px',
    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)',
    display: 'flex',
    flexDirection: 'column' as const,
    overflow: 'hidden',
    zIndex: 9998,
  },

  // Card container
  card: {
    background: 'var(--glyde-background)',
    borderRadius: '12px',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    overflow: 'hidden',
  },

  // Header
  header: {
    padding: '16px 20px',
    borderBottom: '1px solid var(--glyde-border)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },

  // Mode selector
  modeSelector: {
    display: 'flex',
    gap: '8px',
    padding: '4px',
    background: 'var(--glyde-surface)',
    borderRadius: '8px',
  },

  modeSelectorButton: {
    padding: '8px 16px',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '14px',
    fontWeight: 500,
    transition: 'all 0.2s',
  },

  // Transcript area
  transcriptArea: {
    flex: 1,
    overflowY: 'auto' as const,
    padding: '16px',
    background: 'var(--glyde-surface)',
  },

  // Message bubble
  messageBubble: {
    maxWidth: '80%',
    padding: '10px 14px',
    borderRadius: '12px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  },

  messageBubbleUser: {
    background: 'var(--glyde-primary)',
    color: 'var(--glyde-primary-text)',
    borderTopRightRadius: '4px',
  },

  messageBubbleAgent: {
    background: 'var(--glyde-background)',
    color: 'var(--glyde-text)',
    borderTopLeftRadius: '4px',
  },

  // Controls area
  controlsArea: {
    padding: '16px',
    borderTop: '1px solid var(--glyde-border)',
    background: 'var(--glyde-background)',
  },

  // Speaking indicators
  speakingIndicator: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '13px',
  },

  speakingDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    transition: 'background-color 0.2s',
  },

  // Buttons
  primaryButton: {
    padding: '12px 24px',
    background: 'var(--glyde-primary)',
    color: 'var(--glyde-primary-text)',
    border: 'none',
    borderRadius: '8px',
    fontSize: '15px',
    fontWeight: 600,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    transition: 'background-color 0.2s, transform 0.1s',
  },

  secondaryButton: {
    padding: '10px 20px',
    background: 'var(--glyde-surface)',
    color: 'var(--glyde-text)',
    border: '1px solid var(--glyde-border)',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    transition: 'background-color 0.2s',
  },

  dangerButton: {
    padding: '12px 24px',
    background: 'var(--glyde-error)',
    color: 'white',
    border: 'none',
    borderRadius: '24px',
    fontSize: '15px',
    fontWeight: 600,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    transition: 'background-color 0.2s',
  },

  iconButton: {
    width: '44px',
    height: '44px',
    padding: '0',
    border: 'none',
    borderRadius: '50%',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background-color 0.2s',
  },

  // Input field
  inputField: {
    flex: 1,
    padding: '12px 16px',
    borderRadius: '8px',
    border: '1px solid var(--glyde-border)',
    background: 'var(--glyde-background)',
    color: 'var(--glyde-text)',
    fontSize: '14px',
    outline: 'none',
    transition: 'border-color 0.2s',
  },

  // Status badge
  statusBadge: {
    padding: '4px 10px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: 500,
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },

  statusBadgeLive: {
    background: 'var(--glyde-success-light)',
    color: 'var(--glyde-success-text)',
  },

  statusBadgeConnecting: {
    background: 'var(--glyde-primary-light)',
    color: 'var(--glyde-primary)',
  },
};

/**
 * Get position styles for floating widget
 */
export function getPositionStyles(position: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left', offset = 20): { button: React.CSSProperties; panel: React.CSSProperties } {
  const positions = {
    'bottom-right': {
      button: { bottom: offset, right: offset },
      panel: { bottom: offset + 70, right: offset },
    },
    'bottom-left': {
      button: { bottom: offset, left: offset },
      panel: { bottom: offset + 70, left: offset },
    },
    'top-right': {
      button: { top: offset, right: offset },
      panel: { top: offset + 70, right: offset },
    },
    'top-left': {
      button: { top: offset, left: offset },
      panel: { top: offset + 70, left: offset },
    },
  };

  return positions[position];
}

/**
 * Inject global styles into the document
 */
export function injectGlobalStyles(theme: 'light' | 'dark' = 'light'): void {
  const styleId = 'glyde-widget-styles';
  
  // Check if styles are already injected
  if (document.getElementById(styleId)) {
    return;
  }

  const styleElement = document.createElement('style');
  styleElement.id = styleId;
  styleElement.textContent = `
    :root {
      ${getThemeCSSVariables(theme)}
    }

    ${globalStyles}
  `;

  document.head.appendChild(styleElement);
}

/**
 * Update theme CSS variables
 */
export function updateTheme(theme: 'light' | 'dark'): void {
  const styleId = 'glyde-widget-styles';
  const styleElement = document.getElementById(styleId);
  
  if (styleElement) {
    styleElement.textContent = `
      :root {
        ${getThemeCSSVariables(theme)}
      }

      ${globalStyles}
    `;
  } else {
    injectGlobalStyles(theme);
  }
}
